/**
 * ProvenancePill — Inline rendering of a `json-provenance` block.
 *
 * Rendered by MessageContent when it detects a fenced code block with
 * language `json-provenance`. The block body is the JSON output of the
 * EveCare tool `get_field_provenance` verbatim.
 *
 * Layout:
 * - Compact pill with field name + current value + primary source + confidence
 * - Click to expand: all_sources list with evidence (document/email name)
 */

import { useState } from "react";
import {
    InfoCircle,
    File02,
    Mail01,
    Package,
    ChevronDown,
    ChevronRight,
    User01,
    AlertCircle,
} from "@untitledui/icons";

export interface ProvenanceSource {
    source?: string;
    source_type?: string;
    priority?: number;
    value?: unknown;
    updated_at?: string;
    extracted_at?: string;
    document_id?: string | null;
    document_name?: string;
    email_id?: string | null;
    message_id?: string | null;
    email_subject?: string;
    email_from?: string;
}

export interface ProvenanceData {
    success?: boolean;
    error?: string;
    container_id?: string;
    container_number?: string | null;
    field_name?: string;
    field_group?: string | null;
    current_value?: unknown;
    primary_source?: ProvenanceSource | null;
    all_sources?: ProvenanceSource[];
    source_count?: number;
    confidence?: number | null;
    status?: string | null;
    concordance?: boolean | null;
    last_checked_at?: string | null;
}

interface Props {
    data: ProvenanceData;
}

const SOURCE_LABEL: Record<string, string> = {
    manual_user: "Modifica manuale",
    document_extraction: "Estratto da documento",
    jsoncargo: "Tracking carrier",
    email_body: "Corpo email",
    user_inquiry: "Analisi utente",
};

const SOURCE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
    manual_user: User01,
    document_extraction: File02,
    jsoncargo: Package,
    email_body: Mail01,
};

function formatValue(value: unknown): string {
    if (value === null || value === undefined) return "—";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

function formatDate(iso?: string): string {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleString("it-IT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function humanFieldName(field?: string): string {
    if (!field) return "—";
    return field
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function sourceLabel(key?: string): string {
    if (!key) return "sconosciuta";
    return SOURCE_LABEL[key] ?? key;
}

function SourceIcon({ source, className }: { source?: string; className?: string }) {
    const Icon = source ? SOURCE_ICON[source] ?? InfoCircle : InfoCircle;
    return <Icon className={className} />;
}

export const ProvenancePill = ({ data }: Props) => {
    const [expanded, setExpanded] = useState(false);

    if (!data.success) {
        return (
            <div className="my-3 flex items-center gap-2 rounded-lg border border-red-300/50 bg-red-50/50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/20 dark:text-red-300">
                <AlertCircle className="size-4 shrink-0" />
                <span>Provenance non disponibile: {data.error ?? "errore sconosciuto"}</span>
            </div>
        );
    }

    const primary = data.primary_source;
    const primaryKey = primary?.source ?? primary?.source_type;
    const allSources = data.all_sources ?? [];
    const hasMultiple = allSources.length > 1 || (allSources.length === 1 && !primary);
    const confidencePct =
        typeof data.confidence === "number" ? Math.round(data.confidence * 100) : null;

    return (
        <div className="my-3 rounded-lg border border-secondary bg-secondary/30 overflow-hidden">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-secondary/50 transition-colors"
            >
                {hasMultiple ? (
                    expanded ? (
                        <ChevronDown className="size-4 shrink-0 text-tertiary" />
                    ) : (
                        <ChevronRight className="size-4 shrink-0 text-tertiary" />
                    )
                ) : (
                    <span className="size-4 shrink-0" />
                )}
                <SourceIcon source={primaryKey} className="size-4 shrink-0 text-brand" />
                <div className="flex-1 min-w-0 text-sm">
                    <span className="font-medium text-primary">{humanFieldName(data.field_name)}:</span>{" "}
                    <span className="text-primary">{formatValue(data.current_value)}</span>
                    <span className="text-tertiary">
                        {" · "}
                        {sourceLabel(primaryKey)}
                        {primary?.document_name ? ` (${primary.document_name})` : ""}
                        {primary?.email_subject ? ` · ${primary.email_subject}` : ""}
                    </span>
                </div>
                {confidencePct !== null && (
                    <span
                        className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
                            confidencePct >= 75
                                ? "bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300"
                                : confidencePct >= 50
                                  ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-300"
                                  : "bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-300"
                        }`}
                    >
                        {confidencePct}%
                    </span>
                )}
            </button>

            {expanded && hasMultiple && (
                <div className="border-t border-secondary px-3 py-2 space-y-2 bg-primary/5">
                    <div className="text-xs text-tertiary uppercase tracking-wide">
                        Tutte le fonti ({allSources.length})
                        {data.concordance === false && (
                            <span className="ml-2 text-yellow-600 dark:text-yellow-400">
                                · fonti discordi
                            </span>
                        )}
                    </div>
                    {allSources.map((src, idx) => {
                        const key = src.source ?? src.source_type;
                        const when = src.extracted_at ?? src.updated_at;
                        return (
                            <div
                                key={idx}
                                className="flex items-start gap-2 text-sm"
                            >
                                <SourceIcon
                                    source={key}
                                    className="size-4 shrink-0 mt-0.5 text-tertiary"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-primary">
                                        <span className="font-medium">{sourceLabel(key)}</span>
                                        {src.value !== undefined && (
                                            <>
                                                {" → "}
                                                <span>{formatValue(src.value)}</span>
                                            </>
                                        )}
                                    </div>
                                    <div className="text-xs text-tertiary">
                                        {src.document_name && <>📄 {src.document_name}{" · "}</>}
                                        {src.email_subject && <>✉️ {src.email_subject}{" · "}</>}
                                        {src.email_from && <>da {src.email_from}{" · "}</>}
                                        {when && formatDate(when)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
